import {ButtonColour} from '../../../../ui/ButtonColour';
import Tooltip from '../../../../ui/Tooltip';
import PrimaryButton from '../../../../ui/PrimaryButton';
import React from 'react';
import {License, LicenseType} from '../../../../schema';
import gql from 'graphql-tag';
import {useMutation} from '@apollo/client';
import {handleError, handleSuccess} from '../../../../utils/Apollo';
import Modal from '../../../../ui/modal/Modal';
import ModalTitle from '../../../../ui/modal/ModalTitle';
import ModalContent from '../../../../ui/modal/ModalContent';
import ModalButtons from '../../../../ui/modal/ModalButtons';
import TextButton from '../../../../ui/TextButton';
import IconList from "../../../../components/icon/IconList";
import IconInfoLight from "../../../../components/icon/IconInfoLight";
import OldUrls from "../../../../models/utils/OldUrls";
import classNames from "classnames";
import IconCalendar from "../../../../components/icon/IconCalendar";
import IconCertificate from "../../../../components/icon/IconCertificate";
import {UserIcon} from "@heroicons/react/outline";

type Props = {
  license: License
  types: Map<string, LicenseType>
  withSeats: boolean
}

export default function LicenseTile({license, types, withSeats}: Props) {
  const [showDelete, setShowDelete] = React.useState(false);

  const [deleteMe] = useMutation(deleteMutation, {
    refetchQueries: ['GetLicenseTypesQuery', 'licenseTypes']
  });

  const onClose = () => {
    setShowDelete(false);
  }

  const onDelete = React.useCallback(() => {
    deleteMe({
      variables: {id: license.id}
    }).then(
      () => {
        setTimeout(() => {
          window.location.reload();
        }, 500);
        handleSuccess('License deleted')
      },
      (apolloError) => handleError(apolloError)
    );
  }, [deleteMe, license]);


  return (
    <div className={classNames('border-gray border rounded-2xl py-5 px-5 mb-4 flex flex-col justify-between',
      {'text-gray': license.expired})}>
      <div>
        <div className="mt-2 text-xl mb-6">
          {types.get(license.typeId)?.name || 'Unknown license type'}
          {license.expired && <div className="ml-3 text-white bg-red inline rounded-md mt-0.5 px-3 py-0.5 text-sm"
                                   style={{width: "fit-content"}}>Expired</div>
          }
        </div>
        <div className="mb-4">
          <div className="flex mb-4">
            <div>
              <IconCalendar className="text-gray-dark pr-1" height="1.35rem"/> Valid
              till <strong>{license.expiresAt}</strong>
            </div>
          </div>
          <div className="flex mb-4">
            <div>
              <IconCertificate className="text-gray-dark -mb-0.5 pr-1"
                               height="1.25rem"/> Version <strong>{license.version}</strong>
            </div>
          </div>
          {withSeats &&
            <div className="flex mb-4">
              <div>
                <UserIcon className="text-gray-dark pr-1 w-6 h-6 inline"/> Number of seats <strong>{license.seats}</strong>
              </div>
            </div>
          }
          <div className="flex flex-col mb-4">
            <div>
              <IconList className="text-gray-dark -mb-0.5 pr-1" height="1.25rem"/> Subscription periods
              <Tooltip
                content={<div className="w-64">Grey periods are the ones excluded from the number of years as a licensed
                  trainer</div>}
                label="renewal_notifications" className="inline-block ml-1"><IconInfoLight
                className="text-gray" height="1.25rem"/></Tooltip>
            </div>
            <div className="flex flex-col space-y-1 pl-8 mt-2 text-sm">
              {license.periods.map((period, index) => (
                <div key={index} className={classNames('flex', {'text-gray': period.excludeFromExperience})}>
                  <div className="mr-2">{period.start}</div>
                  <div className="mr-2">-</div>
                  <div>{period.end}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-row justify-between">
        <PrimaryButton onClick={() => window.location.href = OldUrls.editLicense(license.id)}>Edit license</PrimaryButton>
        <TextButton onClick={() => setShowDelete(true)} colour={ButtonColour.RED}>Delete</TextButton>
      </div>
      <Modal show={showDelete} onClose={onClose}>
        <ModalTitle title={'Delete license'} onClose={onClose}/>
        <ModalContent>
          <p>Are you sure you want to proceed?</p>
        </ModalContent>
        <ModalButtons>
          <PrimaryButton onClick={onDelete}>Delete</PrimaryButton>
          <TextButton onClick={onClose}>Cancel</TextButton>
        </ModalButtons>
      </Modal>
    </div>
  )
}

const deleteMutation = gql`
  mutation DeleteLicense($id: ID!) {
    deleteLicense(licenseId: $id)
  }
`;
